<template>
  <div class="note-composer">
    <div v-if="title" class="note-composer__title">{{ title }}</div>
    <div v-if="errorMsg" class="note-composer__error">{{ errorMsg }}</div>

    <div v-if="uploadedFile" class="note-composer__file">
      <v-icon size="16" color="accent">mdi-paperclip</v-icon>
      <span class="note-composer__file-name">{{ uploadedFile.fileName }}</span>
      <v-btn icon="mdi-close" variant="text" size="x-small" density="comfortable" @click="clearUploadedFile" />
    </div>
    <div v-if="!uploadedFile" class="note-composer__input-wrap" @paste="onComposerPaste">
      <v-textarea
        ref="textareaRef"
        v-model="newNote"
        class="note-composer__input"
        placeholder="Add a note... type @ to mention a teammate"
        rows="2"
        auto-grow
        hide-details
        @input="onNoteInput"
        @keydown="onNoteKeydown"
        @click="onNoteInput"
        @blur="closeMentionMenu" />

      <div v-if="pasteSuggestion" class="note-composer__table-hint">
        <v-icon size="15" color="primary">mdi-table</v-icon>
        <span class="note-composer__table-hint-text">Table imported</span>
        <v-btn
          icon="mdi-close"
          size="x-small"
          variant="text"
          density="comfortable"
          aria-label="Dismiss table suggestion"
          @click="pasteSuggestion = null" />
      </div>

      <div v-if="mentionMenuOpen" class="mention-menu">
        <div
          v-for="(teammate, index) in mentionCandidates"
          :key="teammate.id"
          class="mention-menu__item"
          :class="{ 'mention-menu__item--active': index === mentionActiveIndex }"
          @mousedown.prevent="selectMention(teammate)">
          <span class="mention-menu__avatar" :style="avatarStyle(teammate)">{{ teammate.initials }}</span>
          <span class="mention-menu__name">{{ teammate.fullName }}</span>
          <span class="mention-menu__email">{{ teammate.email }}</span>
        </div>
        <div v-if="mentionCandidates.length === 0" class="mention-menu__empty">No matching teammates</div>
      </div>
    </div>

    <div v-if="confirmedMentions.length" class="note-composer__mentions">
      <span class="note-composer__mentions-label">Will notify</span>
      <span v-for="teammate in confirmedMentions" :key="teammate.id" class="note-composer__mention-chip">
        <span class="mention-menu__avatar mention-menu__avatar--chip" :style="avatarStyle(teammate)">
          {{ teammate.initials }}
        </span>
        {{ teammate.fullName }}
        <v-icon size="12" class="note-composer__mention-check">mdi-check</v-icon>
        <button
          type="button"
          class="note-composer__mention-remove"
          :aria-label="`Don't notify ${teammate.fullName}`"
          @click="removeMention(teammate.id)">
          <v-icon size="12">mdi-close</v-icon>
        </button>
      </span>
    </div>

    <div class="note-composer__actions">
      <template v-if="!uploadedFile">
        <v-btn
          v-if="!showFileUpload"
          variant="text"
          size="small"
          color="primary"
          prepend-icon="mdi-paperclip"
          @click="showFileUpload = true">
          Attach a file
        </v-btn>
        <v-btn
          v-if="!showFileUpload"
          variant="text"
          size="small"
          color="primary"
          prepend-icon="mdi-table"
          @click="openTableDialog">
          Import table
        </v-btn>
        <UploadFileComponent v-if="showFileUpload" @download-link="onDownloadLink" @upload-error="onUploadError" />
      </template>
      <v-spacer />
      <v-btn
        color="primary"
        variant="flat"
        size="small"
        class="note-composer__submit"
        :disabled="((!newNote || newNote.trim() === '') && !uploadedFile) || isSubmitting"
        :loading="isSubmitting"
        @click="addNote">
        {{ addNoteButtonLabel }}
      </v-btn>
    </div>

    <v-dialog v-model="tableDialogOpen" max-width="760">
      <v-card class="table-dialog">
        <div class="table-dialog__header">
          <span class="table-dialog__title">Import a table</span>
          <v-btn icon="mdi-close" variant="text" size="small" density="comfortable" @click="tableDialogOpen = false" />
        </div>
        <div class="table-dialog__body">
          <div class="table-dialog__hint">Copy the cells in Excel or Google Sheets, then paste them below.</div>
          <div @paste="onTablePaste">
            <v-textarea
              v-model="tableText"
              class="table-dialog__input"
              placeholder="Paste your table here"
              rows="4"
              auto-grow
              hide-details />
          </div>
          <template v-if="tableText.trim() || richRows">
            <template v-if="hasTable">
              <div class="table-dialog__preview-label">
                Preview<span v-if="richRows" class="table-dialog__preview-badge">formatting captured</span>
              </div>
              <div class="table-dialog__preview">
                <StatusNoteText :text="tableContent" />
              </div>
            </template>
            <div v-else class="table-dialog__warning">
              That doesn't look like a table yet — copy the cells directly from your spreadsheet and paste again.
            </div>
          </template>
        </div>
        <div class="table-dialog__actions">
          <v-spacer />
          <v-btn variant="text" size="small" @click="tableDialogOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" size="small" :disabled="!hasTable" @click="insertTable">
            Add to note
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { userNotesService } from "@/services/userNotesService";
import mentionsService from "@/services/mentionsService";
import { useInboxStore } from "@/stores";
import { TAG_COLORS } from "@/constants/tagColors";
import UploadFileComponent from "@/components/UploadFileComponent.vue";
import StatusNoteText from "@/components/StatusNoteText.vue";
import { extractTableFromHtml, replaceTableMarkers, serializeTableMarker } from "@/utils/pastedTable";

const newNote = ref("");
const errorMsg = ref("");
const showFileUpload = ref(false);
const uploadedFile = ref(null);
const isSubmitting = ref(false);
const tableDialogOpen = ref(false);
const tableText = ref("");
const richRows = ref(null);
const richSource = ref("");

const hasTable = computed(
  () =>
    !!richRows.value ||
    tableText.value.split("\n").some((line) => line.split("\t").filter((cell) => cell.trim()).length >= 2)
);

const tableContent = computed(() =>
  richRows.value ? serializeTableMarker(richRows.value) : tableText.value.replace(/\s+$/g, "")
);

function openTableDialog() {
  tableText.value = "";
  richRows.value = null;
  richSource.value = "";
  pasteSuggestion.value = null;
  tableDialogOpen.value = true;
}

function onTablePaste(event) {
  const rows = extractTableFromHtml(event.clipboardData?.getData("text/html"));
  if (!rows) return;
  nextTick(() => {
    richRows.value = rows;
    richSource.value = tableText.value;
  });
}

const pasteSuggestion = ref(null);

function onComposerPaste(event) {
  const rows = extractTableFromHtml(event.clipboardData?.getData("text/html"));
  if (!rows) return;
  event.preventDefault();
  appendToNote(serializeTableMarker(rows));
  pasteSuggestion.value = { snapshot: newNote.value };
}

watch(newNote, (text) => {
  if (pasteSuggestion.value && text !== pasteSuggestion.value.snapshot) pasteSuggestion.value = null;
});

watch(tableText, (text) => {
  if (richRows.value && text !== richSource.value) richRows.value = null;
});

function appendToNote(table) {
  const existing = newNote.value.replace(/\s+$/g, "");
  newNote.value = existing ? `${existing}\n${table}\n` : `${table}\n`;
  nextTick(() => noteTextarea()?.focus());
}

function insertTable() {
  appendToNote(tableContent.value);
  tableDialogOpen.value = false;
}

const inboxStore = useInboxStore();
const textareaRef = ref(null);
const mentionMenuOpen = ref(false);
const mentionQuery = ref("");
const mentionStart = ref(-1);
const mentionActiveIndex = ref(0);
const confirmedMentions = ref([]);

const AVATAR_COLORS = Object.values(TAG_COLORS);
const MENTION_MESSAGE_LIMIT = 500;

const teammates = computed(() =>
  inboxStore.teammates.map((user) => {
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email;
    const initials =
      `${(user.firstName || "").charAt(0)}${(user.lastName || "").charAt(0)}`.toUpperCase() ||
      (user.email || "?").charAt(0).toUpperCase();
    return { id: user.id, fullName, email: user.email, initials, color: AVATAR_COLORS[user.id % AVATAR_COLORS.length] };
  })
);

const mentionCandidates = computed(() => {
  const query = mentionQuery.value.toLowerCase();
  return teammates.value
    .filter((t) => !query || t.fullName.toLowerCase().includes(query) || (t.email || "").toLowerCase().includes(query))
    .slice(0, 6);
});

function avatarStyle(teammate) {
  return {
    background: `color-mix(in srgb, ${teammate.color} 14%, white)`,
    color: teammate.color,
  };
}

function noteTextarea() {
  return textareaRef.value?.$el?.querySelector("textarea") || null;
}

function onNoteInput() {
  const el = noteTextarea();
  if (!el) return;
  const beforeCaret = el.value.slice(0, el.selectionStart);
  const match = beforeCaret.match(/(?:^|\s)@([a-zA-Z]*(?: [a-zA-Z]*)?)$/);
  if (!match || match[1].startsWith(" ")) {
    closeMentionMenu();
    return;
  }
  const query = match[1];
  const hasMatch = teammates.value.some(
    (t) =>
      t.fullName.toLowerCase().includes(query.toLowerCase()) ||
      (t.email || "").toLowerCase().includes(query.toLowerCase())
  );
  if (query.includes(" ") && teammates.value.length > 0 && !hasMatch) {
    closeMentionMenu();
    return;
  }
  mentionStart.value = beforeCaret.length - query.length - 1;
  mentionQuery.value = query;
  mentionActiveIndex.value = 0;
  mentionMenuOpen.value = true;
  inboxStore.fetchTeammates().catch(() => closeMentionMenu());
}

function onNoteKeydown(event) {
  if (!mentionMenuOpen.value) return;
  if (event.key === "ArrowDown") {
    event.preventDefault();
    mentionActiveIndex.value = Math.min(mentionActiveIndex.value + 1, mentionCandidates.value.length - 1);
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    mentionActiveIndex.value = Math.max(mentionActiveIndex.value - 1, 0);
  } else if (event.key === "Enter" || event.key === "Tab") {
    const teammate = mentionCandidates.value[mentionActiveIndex.value];
    if (teammate) {
      event.preventDefault();
      selectMention(teammate);
    }
  } else if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    closeMentionMenu();
  }
}

function selectMention(teammate) {
  const el = noteTextarea();
  if (!el || mentionStart.value < 0) return;
  const text = newNote.value;
  const caret = el.selectionStart;
  const inserted = `@${teammate.fullName} `;
  newNote.value = text.slice(0, mentionStart.value) + inserted + text.slice(caret);
  if (!confirmedMentions.value.some((m) => m.id === teammate.id)) {
    confirmedMentions.value.push(teammate);
  }
  const nextCaret = mentionStart.value + inserted.length;
  closeMentionMenu();
  nextTick(() => {
    const input = noteTextarea();
    if (!input) return;
    input.focus();
    input.setSelectionRange(nextCaret, nextCaret);
  });
}

function removeMention(id) {
  confirmedMentions.value = confirmedMentions.value.filter((m) => m.id !== id);
}

function closeMentionMenu() {
  mentionMenuOpen.value = false;
  mentionQuery.value = "";
  mentionStart.value = -1;
}

watch(newNote, (text) => {
  confirmedMentions.value = confirmedMentions.value.filter((m) => text.includes(`@${m.fullName}`));
});

async function sendMentions(noteContent) {
  const recipients = confirmedMentions.value;
  if (!recipients.length) return;
  if (!props.orderInfoId && !props.intakeOrderId && !props.intakeDocumentId) return;
  try {
    await mentionsService.createMention({
      recipientIds: recipients.map((m) => m.id),
      message: replaceTableMarkers(noteContent).slice(0, MENTION_MESSAGE_LIMIT),
      orderKey: props.orderInfoId || undefined,
      intakeOrderId: props.intakeOrderId || undefined,
      intakeDocumentKey: props.intakeDocumentId || undefined,
    });
    const assigned = recipients
      .map((m) => inboxStore.teammates.find((t) => t.id === m.id))
      .filter(Boolean)
      .map((t) => ({ id: t.id, firstName: t.firstName, lastName: t.lastName, email: t.email }));
    props.onMentionsSent?.(assigned);
    confirmedMentions.value = [];
  } catch {
    errorMsg.value = "Note added, but notifying your teammates failed — add another note to tag them.";
  }
}

const onDownloadLink = (download) => {
  uploadedFile.value = { uploadKey: download.uploadKey, fileName: download.fileName };
  showFileUpload.value = false;
  errorMsg.value = "";
};

const onUploadError = (message) => {
  errorMsg.value = message;
};

const clearUploadedFile = () => {
  uploadedFile.value = null;
};

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  orderInfoId: {
    type: String,
    required: false,
    default: null,
  },
  intakeOrderId: {
    type: Number,
    required: false,
    default: null,
  },
  intakeDocumentId: {
    type: String,
    required: false,
    default: null,
  },
  onAddNote: {
    type: [Function, null],
    required: false,
    default: null,
  },
  onMentionsSent: {
    type: [Function, null],
    required: false,
    default: null,
  },
  overrideAddNote: {
    type: [Function, null],
    required: false,
    default: null,
  },
  addNoteButtonLabel: {
    type: String,
    required: false,
    default: "Add Note",
  },
});

function resetComposer() {
  newNote.value = "";
  uploadedFile.value = null;
  showFileUpload.value = false;
  pasteSuggestion.value = null;
  tableText.value = "";
  richRows.value = null;
  richSource.value = "";
}

const addNote = async () => {
  if (isSubmitting.value) return;
  const noteContent = newNote.value.trim();
  const attachment = uploadedFile.value;
  if (!noteContent && !attachment) return;
  isSubmitting.value = true;
  try {
    errorMsg.value = "";

    if (props.overrideAddNote) {
      const result = await props.overrideAddNote(noteContent, {
        orderInfoId: props.orderInfoId,
        intakeOrderId: props.intakeOrderId,
        intakeDocumentId: props.intakeDocumentId,
        attachmentKey: attachment?.uploadKey || null,
        attachmentFilename: attachment?.fileName || null,
      });
      if (result === false) return;
      await sendMentions(noteContent);
      resetComposer();
      return;
    }
    const createdNote = await userNotesService.createNote({
      orderInfo: props.orderInfoId,
      intakeOrder: props.intakeOrderId,
      intakeDocument: props.intakeDocumentId,
      note: noteContent,
      attachmentKey: attachment?.uploadKey || undefined,
      attachmentFilename: attachment?.fileName || undefined,
    });
    await sendMentions(noteContent);
    resetComposer();
    if (props.onAddNote) {
      props.onAddNote(createdNote);
    }
  } catch (e) {
    errorMsg.value = `Failed to add note: ${e.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style lang="scss" scoped>
.note-composer {
  font-family: $lh_font_family_dm_sans;
  width: 100%;
}

.note-composer__title {
  font-size: 15px;
  font-weight: 600;
  color: $lh_color_navy_dark;
  margin-bottom: 10px;
}

.note-composer__error {
  color: $lh_color_red;
  font-size: 13.5px;
  margin-bottom: 8px;
}

.note-composer__input-wrap {
  position: relative;
}

.note-composer__table-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 3px 6px 3px 10px;
  background: rgba($lh_sidebar_accent, 0.07);
  border: 1px solid rgba($lh_sidebar_accent, 0.2);
  border-radius: 999px;
}

.note-composer__table-hint-text {
  font-size: 12.5px;
  font-weight: 500;
  color: $lh_color_navy_dark;
}

.mention-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-width: 340px;
  z-index: 20;
  background: $lh_dash_surface;
  border: 1px solid $lh_dash_border;
  border-radius: 12px;
  box-shadow: $lh_shadow_lg;
  padding: 4px;
  overflow: hidden;
}

.mention-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background $lh_transition_fast;

  &:hover,
  &--active {
    background: rgba($lh_color_blue, 0.07);
  }
}

.mention-menu__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.mention-menu__avatar--chip {
  width: 16px;
  height: 16px;
  font-size: 7.5px;
}

.mention-menu__name {
  font-size: 13.5px;
  font-weight: 500;
  color: $lh_color_navy_dark;
  white-space: nowrap;
}

.mention-menu__email {
  font-size: 12px;
  color: $lh_color_blue_gray;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mention-menu__empty {
  padding: 10px 12px;
  font-size: 13px;
  color: $lh_color_blue_gray;
}

.note-composer__mentions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.note-composer__mentions-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
}

.note-composer__mention-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: $lh_color_navy_dark;
  background: rgba($lh_sidebar_accent, 0.08);
  border: 1px solid rgba($lh_sidebar_accent, 0.2);
  border-radius: 999px;
  padding: 3px 8px 3px 4px;
}

.note-composer__mention-check {
  color: $lh_sidebar_accent;
}

.note-composer__mention-remove {
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: $lh_color_blue_gray;

  &:hover {
    color: $lh_color_navy_dark;
  }
}

.note-composer__input {
  :deep(.v-field) {
    background: $lh_color_snowy_white;
    border: 1px solid $lh_dash_border;
    border-radius: 12px;
    box-shadow: none;
    font-size: 14px;
    transition: border-color $lh_transition_fast, background $lh_transition_fast, box-shadow $lh_transition_fast;

    textarea {
      font-family: $lh_font_family_dm_sans;
      color: $lh_color_navy;
    }

    &.v-field--focused {
      background: $lh_dash_surface;
      border-color: rgba($lh_color_blue, 0.3);
      box-shadow: 0 0 0 3px rgba($lh_color_blue, 0.08);
    }
  }
}

.note-composer__file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba($lh_color_blue, 0.05);
  border: 1px solid rgba($lh_color_blue, 0.2);
  border-radius: 12px;

  .note-composer__file-name {
    font-size: 13.5px;
    font-weight: 500;
    color: $lh_color_navy;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 auto;
  }
}

.note-composer__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.note-composer__submit {
  min-width: 96px;
}

.table-dialog {
  font-family: $lh_font_family_dm_sans;
  border-radius: 16px;
}

.table-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 0;
}

.table-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: $lh_color_navy_dark;
}

.table-dialog__body {
  padding: 12px 20px 0;
}

.table-dialog__hint {
  font-size: 13px;
  color: $lh_color_blue_gray;
  margin-bottom: 10px;
}

.table-dialog__input {
  :deep(.v-field) {
    background: $lh_color_snowy_white;
    border: 1px solid $lh_dash_border;
    border-radius: 12px;
    box-shadow: none;
    font-size: 13px;

    textarea {
      font-family: $lh_font_mono;
      color: $lh_color_navy;
      white-space: pre;
    }
  }
}

.table-dialog__preview-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: $lh_color_blue_gray;
  margin: 14px 0 6px;
}

.table-dialog__preview-badge {
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba($lh_color_green, 0.12);
  color: #2e7d3b;
  letter-spacing: normal;
  text-transform: none;
  font-weight: 600;
}

.table-dialog__preview {
  border: 1px solid $lh_dash_border;
  border-radius: 12px;
  padding: 10px 12px;
  max-height: 320px;
  overflow: auto;
  background: $lh_dash_surface;
}

.table-dialog__warning {
  margin-top: 12px;
  font-size: 13px;
  color: $lh_color_blue_gray;
  background: rgba($lh_color_navy, 0.04);
  border-radius: 10px;
  padding: 10px 12px;
}

.table-dialog__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 20px 16px;
}
</style>
